/*
 * Copyright (C) 2021 Bosch.IO GmbH. All rights reserved.
 */

import React from 'react';
import styled from 'styled-components';
import { BladeActionButton } from './BladeActionButton';
import { BladeHeaderProps } from './types';

const Header = styled.header`
    display: flex;
    height: 3rem;
    box-sizing: content-box;
    border-bottom: 2px solid transparent;
    overflow: hidden;
`;

const ActiveHeader = styled(Header)`
    color: var(--minor-accent__enabled__front__default);
    border-bottom: 2px solid var(--minor-accent__enabled__front__default);
`;

const TextContainer = styled.div`
    flex: 1;
    position: relative;
`;

const Text = styled.h2`
    margin: 0;
`;

const TextInContainer = styled(Text)`
    display: flex;
    align-items: center;
    position: absolute;
    width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    padding: 0.5rem 1rem;
`;

const Actions = styled.div`
    flex: initial;
    padding: 0.75rem;
`;

const CollapsedTopDown = styled.div`
    width: 1px;
    height: 1px;
    transform: rotate(90deg);
    white-space: nowrap;
    position: absolute;
    bottom: 3rem;
    top: 57px;
    left: 37px;
    i {
        position: relative;
        top: 4px;
    }
`;

const CollapsedBottomUp = styled.div`
    width: 1px;
    height: 1px;
    transform: rotate(270deg);
    white-space: nowrap;
    position: absolute;
    left: 10px;
    top: 3rem;
    direction: rtl;
    margin-top: 10px;
    i {
        position: relative;
        top: 4px;
    }
`;

function noop() {
    // noop
}

export const BladeHeader: React.FC<BladeHeaderProps> = props => {
    const {
        headerTitle,
        header,
        collapsed = false,
        expanded = false,
        showMinButton = true,
        showMaxButton = true,
        showRestoreButton = true,
        showCloseButton = true,
        onCollapse = noop,
        onExpand = noop,
        onRestore = noop,
        onClose = noop,
        onActivate = noop,
        minTooltip = 'Minimize',
        maxTooltip = 'Maximize',
        restoreTooltip = 'Restore',
        closeTooltip = 'Close',
        flip = false,
        active = false,
    } = props;

    const HeaderToUse = active ? ActiveHeader : Header;
    const CollapsedContainer = flip ? CollapsedBottomUp : CollapsedTopDown;

    return (
        <HeaderToUse title={headerTitle}>
            {!collapsed ? (
                <TextContainer onMouseDown={onActivate}>
                    <TextInContainer className={'-size-l'}>
                        {header}
                    </TextInContainer>
                </TextContainer>
            ) : (
                <CollapsedContainer onMouseDown={onActivate}>
                    <Text className={'-size-l'}>{header}</Text>
                </CollapsedContainer>
            )}
            <Actions>
                {showMinButton && !collapsed && !expanded && (
                    <BladeActionButton
                        icon={'less-minimize'}
                        title={minTooltip}
                        onClick={onCollapse}
                    />
                )}
                {showMaxButton && !collapsed && !expanded && (
                    <BladeActionButton
                        icon={'maximize'}
                        title={maxTooltip}
                        onClick={onExpand}
                    />
                )}
                {showRestoreButton && (expanded || collapsed) && (
                    <BladeActionButton
                        icon={collapsed ? 'maximize' : 'minimize'}
                        title={restoreTooltip}
                        onClick={onRestore}
                    />
                )}
                {showCloseButton && !collapsed && !expanded && (
                    <BladeActionButton
                        icon={'close'}
                        title={closeTooltip}
                        onClick={onClose}
                    />
                )}
            </Actions>
        </HeaderToUse>
    );
};
